import { EmptyArrayError, UnknownProductTypeError } from 'utils/errors'
import { NetworkType } from 'abis/set/sets'
import { Product, RawIndexProductResponse, parseProduct } from '../index/product-info/model'
import { ProductOverview } from 'pages/overview/storage/interface'
import { ProductType } from 'sdk'
import { INDEX_PRODUCT_INFO_QUERY } from '../index/product-info/query-params'
import { fetchAllHistory } from '../daily-statistics/query-params'
import {
  RawDailyStatisticsIndexResponse,
  RawDailyStatisticResponse,
  DailyStatistics,
  parseDailyStats,
} from '../index/daily-statistics/model'
import INDEX_DAILY_STATS from 'services/query/query-strings/index-products/daily-statistics'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'

/**
 * Fetches all daily statistics for an index product.
 * @param {string} url The base URL for the fetch request.
 * @param {(now: number) => string} queryParams A function that returns query parameters for the fetch request.
 * @param {string} address The address to fetch statistics for.
 * @param {NetworkType} chain The blockchain network type.
 * @returns {Promise<DailyStatistics>} A promise that resolves to the daily statistics.
 */
export const fetchAllIndexDaily = async (
  url: string,
  queryParams: (now: number) => string,
  address: string,
  chain: NetworkType,
) => {
  return await fetchAllHistory<
    RawDailyStatisticsIndexResponse,
    RawDailyStatisticResponse,
    DailyStatistics
  >(
    url,
    queryParams,
    (res) => {
      return res.indexes[0].dailyStats
    },
    (buffer) => parseDailyStats(buffer, address, chain),
  )
}

/**
 * Returns query parameters for fetching product data based on the product type.
 * @param {ProductType} productType The type of SDK product.
 * @returns {Function} A function that returns query parameters for product data.
 */
export const getParamForProductData = (productType: ProductType) => (address: string) => {
  switch (productType) {
    case ProductType.INDEX_V1:
      return INDEX_PRODUCT_INFO_QUERY(address)
    case ProductType.INDEX_V2:
      return INDEX_PRODUCT_INFO_QUERY(address)
    default:
      throw new UnknownProductTypeError(productType)
  }
}
/**
 * Returns query parameters for fetching daily statistics based on the product type and address.
 * @param {ProductType} productType The type of SDK product.
 * @param {string} address The product address.
 * @returns {Function} A function that returns query parameters for daily statistics.
 */
export const getParamsforDailyStats =
  (productType: ProductType, address: string) => (now: number) => {
    switch (productType) {
      case ProductType.INDEX_V1:
        return INDEX_DAILY_STATS(address, now)
      case ProductType.INDEX_V2:
        return INDEX_DAILY_STATS(address, now)
      default:
        throw new UnknownProductTypeError(productType)
    }
  }
/**
 * Returns a fetcher function for product information based on the product type and network.
 * @param {ProductType} type The type of SDK product.
 * @param {NetworkType} network The network type.
 * @returns {Function} A fetcher function for product information.
 */
export const getProductInfoFetcher = (
  type: ProductType,
  network: NetworkType,
  key: string,
  functionName: string,
) => {
  switch (type) {
    case ProductType.INDEX_V1:
      return async (url: string, params: string) => {
        const response = await fetchGraphQLData<RawIndexProductResponse>(url, params)
        if (!response) {
          throw new Error(
            `Fails in getProductInfoFetcher && ${functionName} functions with a key ${key}`,
          )
        }
        if (response.indexes.length === 0) throw new EmptyArrayError()
        return parseProduct(response.indexes[0], network, key)
      }
    case ProductType.INDEX_V2:
      return async (url: string, params: string) => {
        const response = await fetchGraphQLData<RawIndexProductResponse>(url, params)
        if (!response) {
          throw new Error(
            `Fails in getProductInfoFetcher && ${functionName} functions with a key ${key}`,
          )
        }
        if (response.indexes.length === 0) throw new EmptyArrayError()
        return parseProduct(response.indexes[0], network, key)
      }
    default:
      throw new UnknownProductTypeError(type)
  }
}
/**
 * Generates an overview of a product including price history and APY based on daily statistics.
 * @param {Product} product The product to generate an overview for.
 * @param {DailyStatistics} dailyStats The daily statistics to include in the overview.
 * @returns {ProductOverview} An overview object of the product.
 */
export const generateProductOverview = (
  product: Product,
  dailyStats: DailyStatistics,
): ProductOverview => {
  const overview: ProductOverview = {
    ...product,
    priceHistory: dailyStats.priceHistory,
    isError: dailyStats.isError,
  }
  return overview
}
